import { AxiosResponse } from 'axios';
import {
  BluecoinAssignment,
  BluecoinAssignmentValidationError,
  GetBluecoinApiResponse,
  UpdateBluecoinAssignment,
} from './interfaces';
import { getRequest, patchRequest, postRequest, putRequest } from '../../Services/HttpService';
import { getUserId } from '../../Services/UserService';
import { isDemo } from '../../Services/DemoService';
import JWTService from '../../Services/JWTService';

export interface UserSettingsDateRangePicker {
  status: string;
  settings: {
    id: number;
    key: 'date_range_picker';
    userId: number;
    value: {
      interval: string;
      dateLow: string;
      dateHigh: string;
    };
  };
  token: string;
}

export const activate = async (bluecoinId: (number | string)[]): Promise<any> => {
  const response: AxiosResponse | false = await postRequest(
    `/v1/bluecoin/activate`,
    { bluecoinId },
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data;
  }
  return null;
};

export const deactivate = async (bluecoinId: (number | string)[]): Promise<any> => {
  const response: AxiosResponse | false = await postRequest(
    `/v1/bluecoin/deactivate`,
    { bluecoinId },
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data;
  }
  return null;
};

export const discard = async (bluecoinId: number[]): Promise<any> => {
  const response: AxiosResponse | false = await postRequest(
    `/v1/bluecoin/discard`,
    { bluecoinId },
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data;
  }
  return null;
};

export const get = async (bluecoinId: number | string): Promise<GetBluecoinApiResponse | null> => {
  const response: AxiosResponse | false = await getRequest(`/v1/bluecoin/${bluecoinId}`, {
    returnAxiosResponse: true,
  });
  if (response && response.status === 200 && response.data) {
    return response.data.data satisfies GetBluecoinApiResponse;
  }
  return null;
};

export const closeAssignment = async (assignmentId: number): Promise<BluecoinAssignment | null> => {
  const response: AxiosResponse | false = await getRequest(
    `/v1/bluecoin/assignment/${assignmentId}/close`,
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data.assignment satisfies BluecoinAssignment;
  }
  return null;
};

export const openAssignment = async (assignmentId: number): Promise<BluecoinAssignment | null> => {
  const response: AxiosResponse | false = await getRequest(
    `/v1/bluecoin/assignment/${assignmentId}/open`,
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data.assignment satisfies BluecoinAssignment;
  }
  return null;
};

export const editAssignment = async (
  assignmentId: number,
  data: UpdateBluecoinAssignment
): Promise<BluecoinAssignment | BluecoinAssignmentValidationError | null> => {
  const response: AxiosResponse | false = await putRequest(
    `/v1/bluecoin/assignment/${assignmentId}/edit`,
    data,
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data.assignment satisfies BluecoinAssignment;
  }
  if (response && response.status === 400 && response.data) {
    return response.data satisfies BluecoinAssignmentValidationError;
  }
  return null;
};

export const addAssignment = async (
  data: UpdateBluecoinAssignment
): Promise<BluecoinAssignment | BluecoinAssignmentValidationError | null> => {
  const response: AxiosResponse | false = await postRequest(
    `/v1/bluecoin/assignment/create`,
    data,
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data.assignment satisfies BluecoinAssignment;
  }
  if (response && response.status === 400 && response.data) {
    return response.data satisfies BluecoinAssignmentValidationError;
  }
  return null;
};

export const getCurrencies = async () => {
  const response: AxiosResponse | false = await getRequest(
    `/v1/bluecoin/assignment/form/currencies`,
    { returnAxiosResponse: true }
  );
  if (response && response.data && response.status === 200) {
    return response.data;
  }
  return null;
};

export const getAssignment = async (assignmentId: number): Promise<BluecoinAssignment | null> => {
  const response: AxiosResponse | false = await getRequest(
    `/v1/bluecoin/assignment/${assignmentId}`,
    { returnAxiosResponse: true }
  );
  if (response && response.status === 200 && response.data) {
    return response.data.assignment;
  }
  return null;
};

export const updateMethod = async (
  bluecoinId: number,
  usageMethodStart: string,
  usageMethodEnd: string
): Promise<any> => {
  return patchRequest(`/v1/bluecoin/${bluecoinId}/method`, {
    usageMethodStart,
    usageMethodEnd,
  });
};

export const isValidDate = (dateString: string) => {
  const date = new Date(dateString);
  return !Number.isNaN(date.getTime());
};

export const datepickerSave = async (
  dateValues: Date[],
  interval: string | null
): Promise<UserSettingsDateRangePicker | false> => {
  const userId = getUserId();
  // If is demo mode, just return values without saving
  if (isDemo() && userId) {
    return {
      status: 'ok',
      settings: {
        id: 1,
        key: 'date_range_picker',
        userId,
        value: {
          interval: interval || 'THIS_YEAR',
          dateLow: dateValues[0].toISOString(),
          dateHigh: dateValues[1].toISOString(),
        },
      },
      token: JWTService.getToken(),
    };
  }

  if (dateValues.length === 2) {
    const inputs = {
      interval,
      dateLow: dateValues[0],
      dateHigh: dateValues[1],
    };
    const link = '/v1/user-settings/date_range_picker/save';
    const response = await postRequest(link, inputs);
    return response as UserSettingsDateRangePicker;
  }
  return false;
};
